/**
 * s232 (CHAPTER-TWO-ENGINE-1, reopened as PR A) — chapter two's board data, typed, and its reader.
 *
 * `BOARD_STEPS_CH2` is GENERATED from the committed `Chapter2.dc.html` (`npm run board:generate`) and checked
 * against it on every push (`npm run check:board-parity`), exactly as chapter one's `BOARD_STEPS` is (./board).
 *
 * Chapter two's board has its OWN shape, and it is typed as that shape rather than forced into chapter one's:
 * a screen is a `choice` of `options` (tiles), and a picked tile opens `lines` (stacked questions) or `rows`
 * (a table), whose questions branch with `then`. The closing screen is `kind: 'end'`.
 *
 * 🔴 The same limit as ./board: the board carries look and words, never where an answer is stored.
 */

import { BOARD_STEPS_CH2 } from "./board-steps-ch2.generated";

export type Ch2CellKind = "text" | "person" | "inst" | "address" | "picker" | "seg" | "date" | "will" | "lines" | "break";
export type Ch2Cell = {
  kind: Ch2CellKind;
  k?: string;
  label?: string;
  hint?: string;
  title?: string;
  text?: string;
  ph?: string;
  w?: string;
  opts?: string[];
  org?: boolean;
  long?: boolean;
  big?: boolean;
  inline?: boolean;
  otherLabel?: string;
  otherPh?: string;
  bigTitle?: string;
  bigHint?: string;
  bigPh?: string;
  sample?: string;
  /** A person cell's trigger word ("Name someone"), declared on the board (rule 137). */
  word?: string;
  /** A will cell's two card titles — "In my will" first, then "A wish" (rule 27 words them apart). */
  cardTitles?: [string, string];
  lines?: Ch2Cell[];
  /** A seg or picker's follow-ups, keyed by answer; a person line's follow-ups, shown once someone is named. */
  then?: Record<string, Ch2Cell[]> | Ch2Cell[];
  offWhen?: { k: string; v: string; unless?: { k: string; v: string[] } };
};
export type Ch2Header = { label: string; sub?: string };
export type Ch2Rows = {
  cols: string; start?: number; headers: Ch2Header[]; cells: Ch2Cell[];
  /** s233 (D-233.81, board v440): the pinned home row's own lines, in place of the table's `lines` cell, and its note. */
  pinLines?: Ch2Cell[];
  pinNote?: string;
};
export type Ch2Option = {
  id: string;
  label: string;
  sub?: string;
  grp?: string;
  mark?: string;
  color?: string;
  none?: boolean;
  prune?: number;
  cover?: number;
  lead?: string;
  lines?: Ch2Cell[];
  /** s233 (D-233.81): a tile group's heading ("Where you live" / "Other places"), one of three in its group, the table a
   *  home tile pins its row to (`own` / `rent`), a block drawn on one line, and a table's Add-row words. */
  head?: string;
  one?: boolean;
  pinTo?: string;
  oneLine?: boolean;
  addLabel?: string;
  /** s233 (D-233.16 point 1): the lines' short answers sit two to a line; each pair's follow-ups directly under it. */
  pairs?: boolean;
  rows?: Ch2Rows;
  noteFor?: Record<string, string | Record<string, string>>;
};
export type Ch2Step = {
  kind: "choice" | "end";
  key: string;
  crumb?: string;
  title: string;
  why?: string;
  multi?: boolean;
  options?: Ch2Option[];
  foot?: string;
  /** s233 (rule 153): the step's tiles are 52px. */
  compact?: boolean;
};

export const BOARD_CH2: readonly Ch2Step[] = BOARD_STEPS_CH2 as unknown as readonly Ch2Step[];

/** A chapter-two screen's board declaration. Throws on an unknown key, like `boardStep`. */
export function boardStepCh2(key: string): Ch2Step {
  const s = BOARD_CH2.find((x) => x.key === key);
  if (!s) throw new Error(`board (chapter two): no screen "${key}"`);
  return s;
}

/**
 * A screen's words as the shell shows them. The crumb falls back to the title, as the board's own step pills do
 * (`sp.crumb || sp.title`) — the closing screen carries no crumb.
 */
export function ch2Words(key: string): { key: string; crumb: string; title: string; why?: string } {
  const s = boardStepCh2(key);
  return { key: s.key, crumb: s.crumb ?? s.title, title: s.title, ...(s.why ? { why: s.why } : {}) };
}
